import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDecodedToken } from "./useToken";
import { useUserRole } from "../utils/helperMethods";
import {
  buildAdminSponsorUserManagePath,
  buildAdminVendorUserManagePath,
  mapAssessmentDetailsToOrgRow,
  mapLibraryItemToOrgRow,
  mapRfiDetailsToOrgRow,
  resolveAdminManageBackendUserPath,
  resolveGridSponsorOrgHref,
  resolveGridVendorOrgHref,
  toVendorOrgRow,
} from "../utils/gridOrgNameLinks";
import { getUserType } from "../utils/helperMethods";

export function useGridOrgNameNavigation() {
  const navigate = useNavigate();
  const userRole = useUserRole();
  const { decoded } = useDecodedToken();

  const viewerSponsorId =
    decoded?.sponsor_id ?? decoded?.Sponsor_Id ?? null;
  const viewerProviderId =
    decoded?.provider_id ?? decoded?.Provider_Id ?? null;

  const navigateToHref = useCallback(
    (href) => {
      if (href) navigate(href);
    },
    [navigate]
  );

  const sponsorHrefForRow = useCallback(
    (rowSponsorId) =>
      resolveGridSponsorOrgHref({
        viewerRole: userRole,
        viewerSponsorId,
        rowSponsorId,
      }),
    [userRole, viewerSponsorId]
  );

  const vendorHrefForRow = useCallback(
    (item) =>
      resolveGridVendorOrgHref({
        viewerRole: userRole,
        viewerProviderId,
        rowProviderId: item?.provider_id ?? item?.providerId,
        rowItem: toVendorOrgRow(item),
      }),
    [userRole, viewerProviderId]
  );

  const sponsorHrefForLibraryItem = useCallback(
    (item) =>
      sponsorHrefForRow(
        item?.sponsor_id ?? item?.sponsorId ?? mapLibraryItemToOrgRow(item).sponsor_id
      ),
    [sponsorHrefForRow]
  );

  const vendorHrefForLibraryItem = useCallback(
    (item) => vendorHrefForRow(mapLibraryItemToOrgRow(item)),
    [vendorHrefForRow]
  );

  const vendorHrefForRfiDetails = useCallback(
    (details) => vendorHrefForRow(mapRfiDetailsToOrgRow(details)),
    [vendorHrefForRow]
  );

  const sponsorHrefForRfiDetails = useCallback(
    (details) =>
      sponsorHrefForRow(
        details?.sponsor_id ??
          details?.sponsorId ??
          mapRfiDetailsToOrgRow(details).sponsor_id
      ),
    [sponsorHrefForRow]
  );

  const vendorHrefForAssessmentDetails = useCallback(
    (details) => vendorHrefForRow(mapAssessmentDetailsToOrgRow(details)),
    [vendorHrefForRow]
  );

  const sponsorHrefForAssessmentDetails = useCallback(
    (details) =>
      sponsorHrefForRow(mapAssessmentDetailsToOrgRow(details).sponsor_id),
    [sponsorHrefForRow]
  );

  const userRowManageHref = useCallback(
    (row, { orgKind = "vendor" } = {}) => {
      const viewerType = getUserType(userRole);
      if (viewerType !== "admin") return null;
      if (orgKind === "sponsor") {
        return buildAdminSponsorUserManagePath(row);
      }
      if (orgKind === "backend") {
        return resolveAdminManageBackendUserPath(row?.id);
      }
      return buildAdminVendorUserManagePath(row);
    },
    [userRole]
  );

  return useMemo(
    () => ({
      navigateToHref,
      sponsorHrefForRow,
      vendorHrefForRow,
      sponsorHrefForLibraryItem,
      vendorHrefForLibraryItem,
      vendorHrefForRfiDetails,
      sponsorHrefForRfiDetails,
      vendorHrefForAssessmentDetails,
      sponsorHrefForAssessmentDetails,
      userRowManageHref,
    }),
    [
      navigateToHref,
      sponsorHrefForRow,
      vendorHrefForRow,
      sponsorHrefForLibraryItem,
      vendorHrefForLibraryItem,
      vendorHrefForRfiDetails,
      sponsorHrefForRfiDetails,
      vendorHrefForAssessmentDetails,
      sponsorHrefForAssessmentDetails,
      userRowManageHref,
    ]
  );
}
